import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Actor, FamilyDetail, FamilyMember, FamilySummary, MeResponse } from '@app/shared';
import { api, ApiError, OfflineError, setUnauthorizedHandler } from './api.ts';
import { reconcileAppearance, saveAppearance } from './appearance.ts';
import { getMeta, META, setMeta, wipeAll, wipeFamily } from './db.ts';
import { disablePush } from './notifications.ts';
import { getPrefs, setPrefsUser, updatePrefs } from './prefs.ts';
import { syncEngine } from './sync/engine.ts';

type Status = 'loading' | 'signed-out' | 'signed-in';

export interface SessionValue {
  status: Status;
  me: MeResponse | null;
  /** Detail of the active family (members, settings); cached for offline use. */
  family: FamilyDetail | null;
  activeFamily: FamilySummary | null;
  actor: Actor | null;
  /** Why the last session ended, for a friendly message on the sign-in screen. */
  endedReason: string | null;
  setSignedIn(me: MeResponse): Promise<void>;
  signOut(): Promise<void>;
  refreshMe(): Promise<void>;
  refreshFamily(): Promise<void>;
  setActiveFamily(id: string): void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [family, setFamily] = useState<FamilyDetail | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [endedReason, setEndedReason] = useState<string | null>(null);
  const meRef = useRef<MeResponse | null>(null);

  const pickFamily = useCallback((next: MeResponse) => {
    const preferred = getPrefs().lastFamilyId;
    const id = next.families.find((f) => f.id === preferred)?.id ?? next.families[0]?.id ?? null;
    setActiveId(id);
    return id;
  }, []);

  const applyMe = useCallback(
    async (next: MeResponse) => {
      const cached = meRef.current ?? (await getMeta<MeResponse>(META.me));
      if (cached && cached.user.id !== next.user.id) await wipeAll();
      // Families the user no longer belongs to are removed from this device.
      if (cached && cached.user.id === next.user.id) {
        for (const f of cached.families) if (!next.families.some((n) => n.id === f.id)) await wipeFamily(f.id);
      }
      meRef.current = next;
      setPrefsUser(next.user.id);
      if (next.preferences) reconcileAppearance(next.preferences);
      await setMeta(META.me, next);
      setMe(next);
      pickFamily(next);
      setStatus('signed-in');
    },
    [pickFamily],
  );

  const endSession = useCallback(async (reason: string | null, wipe: boolean) => {
    syncEngine.stop();
    if (wipe) await wipeAll();
    meRef.current = null;
    setMe(null);
    setFamily(null);
    setActiveId(null);
    setPrefsUser(null);
    setEndedReason(reason);
    setStatus('signed-out');
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      await applyMe(await api<MeResponse>('/api/auth/me'));
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
    }
  }, [applyMe]);

  useEffect(() => {
    // A revoked session (signed out elsewhere, password reset, removed) wipes
    // this device; an expired one keeps local data until the same person signs in again.
    setUnauthorizedHandler((reason) => {
      if (!meRef.current) return;
      const wipe = reason === 'revoked' || reason === 'invalid';
      void endSession(wipe ? 'Your session ended. Please sign in again.' : 'Please sign in again to keep syncing.', wipe);
    });
    let cancelled = false;
    (async () => {
      const cached = await getMeta<MeResponse>(META.me);
      try {
        const fresh = await api<MeResponse>('/api/auth/me');
        if (!cancelled) await applyMe(fresh);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof OfflineError && cached) {
          // Offline start: the replica on this device is still the user's.
          meRef.current = cached;
          setPrefsUser(cached.user.id);
          setMe(cached);
          pickFamily(cached);
          setStatus('signed-in');
        } else {
          setStatus('signed-out');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyMe, endSession, pickFamily]);

  const refreshFamily = useCallback(async () => {
    if (!activeId) return setFamily(null);
    try {
      const detail = await api<FamilyDetail>(`/api/families/${activeId}`);
      await setMeta(META.family(activeId), detail);
      setFamily(detail);
    } catch (err) {
      if (err instanceof OfflineError) {
        setFamily((await getMeta<FamilyDetail>(META.family(activeId))) ?? null);
      } else if (err instanceof ApiError && err.status === 404) {
        await wipeFamily(activeId);
        await refreshMe();
      }
    }
  }, [activeId, refreshMe]);

  useEffect(() => {
    if (status !== 'signed-in') return;
    void (async () => {
      if (activeId) {
        const cached = await getMeta<FamilyDetail>(META.family(activeId));
        if (cached) setFamily(cached);
      }
      await refreshFamily();
    })();
  }, [status, activeId, refreshFamily]);

  // Appearance changes made offline are saved to the account after reconnecting.
  useEffect(() => {
    if (status !== 'signed-in') return;
    const onOnline = () => void saveAppearance().catch(() => undefined);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [status]);

  // Run the sync engine while signed in.
  useEffect(() => {
    if (status !== 'signed-in' || !me) return;
    syncEngine.start({
      families: me.families,
      onMembershipChange: () => {
        void refreshMe();
        void refreshFamily();
      },
      onFamilyGone: async (familyId) => {
        await wipeFamily(familyId);
        await refreshMe();
      },
    });
    return () => syncEngine.stop();
  }, [status, me, refreshMe, refreshFamily]);

  const value = useMemo<SessionValue>(() => {
    const activeFamily = me?.families.find((f) => f.id === activeId) ?? null;
    return {
      status,
      me,
      family: family && family.id === activeId ? family : null,
      activeFamily,
      actor: me && activeFamily ? { userId: me.user.id, role: activeFamily.role } : null,
      endedReason,
      setSignedIn: async (next) => {
        setEndedReason(null);
        await applyMe(next);
      },
      signOut: async () => {
        // Stop this device receiving the person's push notifications before the next person uses it.
        await disablePush().catch(() => undefined);
        try {
          await api('/api/auth/signout', { method: 'POST' });
        } catch {
          // Even offline, this device forgets the session.
        }
        await endSession(null, true);
      },
      refreshMe,
      refreshFamily,
      setActiveFamily: (id) => {
        updatePrefs((p) => ({ ...p, lastFamilyId: id }));
        setActiveId(id);
      },
    };
  }, [status, me, family, activeId, endedReason, applyMe, endSession, refreshMe, refreshFamily]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

export interface ActiveFamily {
  familyId: string;
  family: FamilyDetail | null;
  summary: FamilySummary;
  actor: Actor;
  members: FamilyMember[];
  member(userId: string | null | undefined): FamilyMember | undefined;
  nameOf(userId: string | null | undefined): string;
  isManager: boolean;
  isChild: boolean;
}

/** For screens inside the signed-in shell, where an active family always exists. */
export function useFamily(): ActiveFamily {
  const s = useSession();
  if (!s.activeFamily || !s.actor || !s.me) throw new Error('useFamily requires an active family');
  const members = s.family?.members ?? [
    {
      userId: s.me.user.id,
      displayName: s.me.user.displayName,
      role: s.activeFamily.role,
      color: s.activeFamily.color,
      birthday: s.me.user.birthday,
      isManagedChild: s.me.user.isManagedChild,
      joinedAt: '',
    },
  ];
  const byId = new Map(members.map((m) => [m.userId, m]));
  return {
    familyId: s.activeFamily.id,
    family: s.family,
    summary: s.activeFamily,
    actor: s.actor,
    members,
    member: (id) => (id ? byId.get(id) : undefined),
    nameOf: (id) => (id ? (byId.get(id)?.displayName ?? 'Former member') : 'Someone'),
    isManager: s.activeFamily.role === 'manager',
    isChild: s.activeFamily.role === 'child',
  };
}
